import { baseRouteId, ferryRawId, isFerryId, isNjBusId, isRailId, njBusRawId, railRawId } from '@traintopia/domain'
import type { FerryRoute, NjBusRoute, RailRoute } from '@traintopia/contracts'

export { baseRouteId, compareRoutes } from '@traintopia/domain'

// Base palette for dark UI theme matching index.css CSS variables.

export const Colors = {
  /** Panel background color */
  ground: '#000000',

  background: '#181d27',       // --bjl-color-background (card surface; what bjl Card paints)
  surface: '#181d27',          // card surface, for call sites that ask for it by this name
  surfaceElevated: '#1c212c',  // --bjl-color-surface-alt (chips, pills, inset controls)
  border: 'rgba(255,255,255,0.08)',      // --bjl-color-border (hairline between rows)
  borderStrong: 'rgba(255,255,255,0.2)', // --bjl-color-border-dark (outlined chips)

  primary: '#4b9df8',          // --bjl-color-primary (the redesign's blue: switches, selected tiles)
  primaryDim: '#3d86db',
  danger: '#ff453a',           // --bjl-color-error (red)
  warning: '#ffb100',          // --bjl-color-warning (amber, also the LED rollsign's own color)
  success: '#32d74b',          // --bjl-color-success (green)

  text: '#ffffff',             // --bjl-color-text
  textSecondary: 'rgba(255,255,255,0.55)', // --bjl-color-text-secondary
  textMuted: 'rgba(255,255,255,0.45)',     // --bjl-color-text-disabled

  subway: {
    '1': '#EE352E', '2': '#EE352E', '3': '#EE352E',
    '4': '#00933C', '5': '#00933C', '6': '#00933C', '6X': '#00933C',
    '7': '#B933AD', '7X': '#B933AD',
    'A': '#0039A6', 'C': '#0039A6', 'E': '#0039A6',
    'B': '#FF6319', 'D': '#FF6319', 'F': '#FF6319', 'FX': '#FF6319', 'M': '#FF6319',
    'G': '#6CBE45',
    'J': '#996633', 'Z': '#996633',
    'L': '#A7A9AC',
    'N': '#FCCC0A', 'Q': '#FCCC0A', 'R': '#FCCC0A', 'W': '#FCCC0A',
    'S': '#808183', 'GS': '#808183', 'FS': '#808183', 'H': '#808183',
    'SI': '#0039A6',
  } as Record<string, string>,

  bus: '#006CB7',
  /** NYC Ferry's brand navy: a ferry route whose colour hasn't loaded yet. */
  ferry: '#1D3A6B',
  /** A LIRR / Metro-North branch whose colour hasn't loaded yet: the MTA's blue. */
  rail: '#0039A6',
  /** NJ Transit's blue — and, since its feed colours every route black, every NJ bus route. */
  njbus: '#1A4B8C',
  /** NJ Transit's orange: the accent that tells an NJ Transit badge from an MTA one. */
  njbusAccent: '#F7941D',

  // Scrims over the map. Neutral black, matching the panels above them; no tint anywhere.
  overlay: 'rgba(0,0,0,0.72)',
  overlayLight: 'rgba(0,0,0,0.45)',
} as const

// ── NYC Ferry ────────────────────────────────────────────────────────────────
// Ferry routes are not the MTA's, so their colours are not written down here: they come
// from NYC Ferry's own GTFS (route_color), through /api/ferry/network, and are registered
// the moment that loads. Every helper below reads this table, so a ferry id resolves to
// its real colour, text colour and short name wherever a route id is drawn.
//
// ⚠️ A badge rasterised BEFORE registration would be navy forever (the badge caches never
// invalidate), which is why the map draws no ferry at all until the network has loaded.
const ferryRoutes = new Map<string, FerryRoute>()

export function registerFerryRoutes(routes: readonly FerryRoute[]): void {
  for (const r of routes) ferryRoutes.set(r.id, r)
}

/** Whether a route id is an NYC Ferry route (`FERRY-ER`). */
export function isFerryRoute(routeId: string): boolean {
  return isFerryId(routeId)
}

export function getFerryRoute(routeId: string): FerryRoute | undefined {
  return ferryRoutes.get(routeId)
}

// ── LIRR + Metro-North ───────────────────────────────────────────────────────
// Same arrangement as the ferry's: branch colours, text colours and codes come from the
// railroads' own GTFS through /api/rail/network and are registered when it loads (see
// store/rail.ts), and the map draws no train until they have been. Ids are namespaced
// (`LIRR-1`, `MNR-2`), so none of these branches can shadow a subway or bus route.
const railRoutes = new Map<string, RailRoute>()

export function registerRailRoutes(routes: readonly RailRoute[]): void {
  for (const r of routes) railRoutes.set(r.id, r)
}

/** Whether a route id is a LIRR or Metro-North branch (`LIRR-1`, `MNR-2`). */
export function isRailRoute(routeId: string): boolean {
  return isRailId(routeId)
}

export function getRailRoute(routeId: string): RailRoute | undefined {
  return railRoutes.get(routeId)
}

// ── NJ Transit buses ─────────────────────────────────────────────────────────
// The railroads' arrangement again: route numbers and colours come from NJ Transit's GTFS
// through /api/njbus/network, registered when it loads (store/njbus.ts). The id a rider never
// sees (`NJB-12`) is not the number on the bus (`113`), so a badge MUST go through
// `routeBadgeLabel` — which is also why the map draws no NJ bus until the network is in.
const njBusRoutes = new Map<string, NjBusRoute>()

export function registerNjBusRoutes(routes: readonly NjBusRoute[]): void {
  for (const r of routes) njBusRoutes.set(r.id, r)
}

/** Whether a route id is an NJ Transit bus route (`NJB-12`). */
export function isNjBusRoute(routeId: string): boolean {
  return isNjBusId(routeId)
}

export function getNjBusRoute(routeId: string): NjBusRoute | undefined {
  return njBusRoutes.get(routeId)
}

export function getRouteColor(routeId: string): string {
  if (isRailId(routeId)) return railRoutes.get(routeId)?.color ?? Colors.rail
  if (isNjBusId(routeId)) return njBusRoutes.get(routeId)?.color ?? Colors.njbus
  return Colors.subway[routeId] ??
    (isFerryId(routeId) ? ferryRoutes.get(routeId)?.color ?? Colors.ferry : Colors.bus)
}

/** Returns route bullet label text for GTFS route ID. */
export function routeBadgeLabel(routeId: string): string {
  if (routeId === 'GS' || routeId === 'FS' || routeId === 'H') return 'S'
  if (isFerryId(routeId)) return ferryRoutes.get(routeId)?.shortName ?? ferryRawId(routeId)
  if (isRailId(routeId)) return railRoutes.get(routeId)?.shortName ?? railRawId(routeId)
  if (isNjBusId(routeId)) return njBusRoutes.get(routeId)?.shortName ?? njBusRawId(routeId)
  return baseRouteId(routeId)
}

/** Broadway BMT. The one MTA line color whose bullet carries black letters. */
export const MTA_BULLET_YELLOW = '#FCCC0A'

/** Returns text color for route bullet (black for Broadway yellow, white otherwise). */
export function getRouteTextColor(routeId: string): string {
  const ferry = isFerryId(routeId) ? ferryRoutes.get(routeId) : undefined
  if (ferry) return ferryTextColor(ferry.color, ferry.textColor)
  // The railroads publish text colours too, with the same readability floor applied.
  const rail = isRailId(routeId) ? railRoutes.get(routeId) : undefined
  if (rail) return ferryTextColor(rail.color, rail.textColor)
  const njBus = isNjBusId(routeId) ? njBusRoutes.get(routeId) : undefined
  if (njBus) return ferryTextColor(njBus.color, njBus.textColor)
  return getRouteColor(routeId) === MTA_BULLET_YELLOW ? '#000' : '#fff'
}

function relativeLuminance(hex: string): number {
  const n = parseInt(hex.replace('#', ''), 16)
  const lin = (c: number) => {
    const v = c / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255)
}

/**
 * A ferry route's badge text: the feed's own text colour, unless it is unreadable on the
 * route colour. Unlike the MTA's rule above (data, applied as published), this one needs a
 * floor, because the feed publishes South Brooklyn's `#FFD100` with WHITE text — about
 * 1.4:1, a blank yellow disc on a phone in daylight. Below 3:1 (WCAG's large-text minimum)
 * it switches to whichever of black and white reads better.
 */
export function ferryTextColor(color: string, published: string): string {
  const contrast = (a: string, b: string) => {
    const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x)
    return (hi + 0.05) / (lo + 0.05)
  }
  if (contrast(color, published) >= 3) return published
  return contrast(color, '#000000') >= contrast(color, '#FFFFFF') ? '#000000' : '#FFFFFF'
}

// True for subway line IDs, false for bus routes.
export function isSubwayRoute(routeId: string): boolean {
  return routeId in Colors.subway
}

// The service name each line runs under — the top line of the R160's destination panel
// ("Broadway Local" over the R's bullet). MTA data, so it is written down once here beside
// the colors rather than assembled at the call site, and in the wording the SIGN uses:
// abbreviated "Av"/"St"/"Blvd", not GTFS's "7 Avenue Express". The express variants keep
// their own entry because that is the whole distinction they exist to carry.
const SUBWAY_ROUTE_NAMES: Record<string, string> = {
  '1': 'Broadway–7 Av Local',
  '2': '7 Av Express', '3': '7 Av Express',
  '4': 'Lexington Av Express', '5': 'Lexington Av Express',
  '6': 'Lexington Av Local', '6X': 'Lexington Av Express',
  '7': 'Flushing Local', '7X': 'Flushing Express',
  'A': '8 Av Express', 'C': '8 Av Local', 'E': '8 Av Local',
  'B': '6 Av Express', 'D': '6 Av Express',
  'F': 'Queens Blvd–6 Av Local', 'FX': 'Queens Blvd–6 Av Express',
  'M': 'Myrtle–6 Av Local',
  'G': 'Brooklyn–Queens Crosstown',
  'J': 'Nassau St Local', 'Z': 'Nassau St Express',
  'L': '14 St–Canarsie Local',
  'N': 'Broadway Express', 'Q': 'Broadway Express',
  'R': 'Broadway Local', 'W': 'Broadway Local',
  'GS': '42 St Shuttle', 'FS': 'Franklin Av Shuttle', 'H': 'Rockaway Park Shuttle',
  'S': 'Shuttle',
  'SI': 'Staten Island Railway',
}

/**
 * The line's service name, or `''` for anything without one — every bus, and any route id
 * the feed invents. Callers print nothing rather than a placeholder: the destination panel
 * reads fine without this line and wrong with "Unknown" in it.
 */
export function getRouteLongName(routeId: string): string {
  return SUBWAY_ROUTE_NAMES[routeId] ?? ferryRoutes.get(routeId)?.longName ??
    railRoutes.get(routeId)?.longName ?? njBusRoutes.get(routeId)?.longName ?? ''
}

/** Checks if two route IDs belong to same route family. */
export function isSameRouteFamily(a: string, b: string): string {
  return baseRouteId(a) === baseRouteId(b) ? baseRouteId(a) : ''
}

export function sameRouteFamily(a: string, b: string): boolean {
  return baseRouteId(a) === baseRouteId(b)
}

// The 6X, 7X and FX are the diamond services of the 6, 7 and F, published as their own
// route ids. They share the line's color, corridor and shape; a rider calls both "the 7".
const EXPRESS_VARIANTS: Record<string, string> = { '6': '6X', '7': '7X', 'F': 'FX' }

/** Returns all route IDs for a route family (local and express). */
export function routeFamily(routeId: string): string[] {
  const base = baseRouteId(routeId)
  const express = EXPRESS_VARIANTS[base]
  return express ? [base, express] : [base]
}

// ---------------------------------------------------------------------------
// Analysis overlay palette
// ---------------------------------------------------------------------------
// Avoids the MTA route colors' saturated reds/greens, so a recolored vehicle never reads as
// "that's the 4 train".

/** Color for trains running ahead of schedule. */
export const EARLY_COLOR = '#38bdf8'
export const DELAY_EARLY_COLOR = EARLY_COLOR
export const DELAY_EARLY_THRESHOLD_SECONDS = -60

interface DelayBand { maxSeconds: number; color: string; label: string }

export const DELAY_BANDS: DelayBand[] = [
  { maxSeconds: -60,       color: EARLY_COLOR, label: 'Ahead' },
  { maxSeconds: 120,       color: '#22c55e', label: 'On time' },
  { maxSeconds: 300,       color: '#eab308', label: '1-5 min late' },
  { maxSeconds: 600,       color: '#f97316', label: '5-10 min late' },
  { maxSeconds: Infinity,  color: '#ef4444', label: '10+ min late' },
]

/** Color for vehicles without scheduled delay info. */
export const DELAY_UNKNOWN_COLOR = '#6b7280'

export function delayColor(delaySeconds: number | undefined): string {
  if (delaySeconds === undefined) return DELAY_UNKNOWN_COLOR
  if (delaySeconds < DELAY_EARLY_THRESHOLD_SECONDS) return EARLY_COLOR
  const late = Math.max(0, delaySeconds)
  return (DELAY_BANDS.find((b) => late <= b.maxSeconds) ?? DELAY_BANDS[DELAY_BANDS.length - 1]).color
}

// ---------------------------------------------------------------------------
// Bus line categorisation
// ---------------------------------------------------------------------------
// Bus route ids carry a borough prefix, so lines group by borough, which keeps a list of
// hundreds readable. Shared by the Routes and Favorites panels.

// Display order for borough sections (most-used boroughs first, express/other last).
export const BUS_BOROUGH_ORDER = [
  'Manhattan', 'Brooklyn', 'Queens', 'Bronx', 'Staten Island', 'Express', 'Other',
] as const

export function busBorough(routeId: string): string {
  const id = routeId.toUpperCase()
  // BM# (Brooklyn), QM# (Queens), BXM# (Bronx), SIM# (Staten Island), X# (legacy).
  if (/^(BM|QM|BXM|SIM)\d/.test(id) || /^X\d/.test(id)) return 'Express'
  if (id.startsWith('BX')) return 'Bronx'
  if (id.startsWith('SI') || id.startsWith('S')) return 'Staten Island'
  if (id.startsWith('B')) return 'Brooklyn'
  if (id.startsWith('M')) return 'Manhattan'
  if (id.startsWith('Q')) return 'Queens'
  return 'Other'
}
